import React, { createContext, useContext, useCallback, useState } from "react";
import styled from "styled-components";
import { theme } from "../theme";

const ConfirmContext = createContext(null);

export const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error("useConfirm must be used within a ConfirmProvider");
  }
  return context;
};

export const ConfirmProvider = ({ children }) => {
  const [confirmState, setConfirmState] = useState(null);
  const [resolveRef, setResolveRef] = useState(null);

  const confirm = useCallback(({ title, message }) => {
    return new Promise((resolve) => {
      setResolveRef(() => resolve);
      setConfirmState({ title, message });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    resolveRef?.(true);
    setConfirmState(null);
    setResolveRef(null);
  }, [resolveRef]);

  const handleCancel = useCallback(() => {
    resolveRef?.(false);
    setConfirmState(null);
    setResolveRef(null);
  }, [resolveRef]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {confirmState && (
        <ConfirmDialog
          title={confirmState.title}
          message={confirmState.message}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </ConfirmContext.Provider>
  );
};

// Inline dialog component to avoid circular dependency

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3000;
`;

const Dialog = styled.div`
  background: ${theme.colors.surface};
  padding: 24px;
  border-radius: ${theme.radius.lg}px;
  width: 100%;
  max-width: 400px;
  box-shadow: ${theme.shadows.lg};
`;

const Title = styled.h3`
  margin: 0 0 8px;
  font-size: 18px;
  color: ${theme.colors.text};
  font-family: ${theme.fonts.body};
`;

const Message = styled.p`
  margin: 0 0 20px;
  font-size: 14px;
  color: ${theme.colors.textSecondary};
  font-family: ${theme.fonts.body};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const CancelButton = styled.button`
  padding: 8px 16px;
  background: ${theme.colors.borderLight};
  color: ${theme.colors.text};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.md}px;
  cursor: pointer;
  font-size: 14px;
  font-family: ${theme.fonts.body};

  &:hover {
    background: ${theme.colors.border};
  }
`;

const ConfirmButton = styled.button`
  padding: 8px 16px;
  background: ${theme.colors.danger};
  color: #fff;
  border: none;
  border-radius: ${theme.radius.md}px;
  cursor: pointer;
  font-size: 14px;
  font-family: ${theme.fonts.body};

  &:hover {
    background: ${theme.colors.dangerHover};
  }
`;

const ConfirmDialog = ({ title, message, onConfirm, onCancel }) => {
  return (
    <Overlay onClick={onCancel}>
      <Dialog onClick={(e) => e.stopPropagation()}>
        <Title>{title}</Title>
        <Message>{message}</Message>
        <ButtonGroup>
          <CancelButton onClick={onCancel}>Отмена</CancelButton>
          <ConfirmButton onClick={onConfirm}>Подтвердить</ConfirmButton>
        </ButtonGroup>
      </Dialog>
    </Overlay>
  );
};
